import React, { useEffect, useState } from 'react'
import { IconArrowLeft, IconDownload } from '@tabler/icons-react'
import Typography from '../../atoms/Typography'
import Loader from '../../atoms/Loader'
import SandboxedFrame from '../../molecules/SandboxedFrame'
import { api } from '../../../api/root'
import { DataRoomDocument } from '../../../types/projects'
import { GradientBorderButton } from '../../atoms/buttons/GradientBorderButton'
import { splitFileName } from '../../../enviroment/lib/fileUtils'
import { ExtBadge } from '../../atoms/ExtBadge'
import { formatDate } from '../../../enviroment/lib/formatters/format-date'
import { formatFileSize } from '../../../enviroment/lib/formatters/format-file-size'
import { previewKind, PreviewKind } from '../../../enviroment/utils/previewKind'
import DataRoomPdfPreview from './DataRoomPdfPreview'
import FullscreenPreviewDialog from '../dialogs/FullscreenPreviewDialog'
import FullScreenButton from '../../atoms/buttons/FullScreenButton'

const PreviewUnavailable: React.FC<{ reason: string }> = ({ reason }) => (
  <div className="flex h-full flex-col items-center justify-center gap-2 bg-gray-900 px-6 text-center">
    <Typography variant="sm-bold" className="text-gray-50">
      Preview unavailable
    </Typography>
    <Typography variant="sm" className="text-gray-200">
      {reason}
    </Typography>
  </div>
)

// unlike the cross-origin frame, an image reports its own failure, so a dead sas becomes a real message
const PreviewImage: React.FC<{ url: string; title: string }> = ({ url, title }) => {
  const [hasFailed, setHasFailed] = useState(false)

  // the cached sas keeps the same url for 30min, so reopening a document must clear a stale failure
  useEffect(() => {
    setHasFailed(false)
  }, [url])

  if (hasFailed) {
    return <PreviewUnavailable reason="This image couldn't be loaded. Download it to view the contents." />
  }

  return (
    <div className="flex h-full w-full items-center justify-center bg-gray-900">
      <img src={url} alt={title} className="max-h-full max-w-full object-contain" onError={() => setHasFailed(true)} />
    </div>
  )
}

// pdf.js reports a failed parse, so a corrupt file gets the same honest message as a broken image
const PreviewPdf: React.FC<{ url: string }> = ({ url }) => {
  const [hasFailed, setHasFailed] = useState(false)

  useEffect(() => {
    setHasFailed(false)
  }, [url])

  if (hasFailed) {
    return <PreviewUnavailable reason="This file couldn't be previewed. Download it to view the contents." />
  }

  return <DataRoomPdfPreview url={url} onLoadError={() => setHasFailed(true)} />
}

interface PreviewBodyProps {
  kind: PreviewKind
  isEmptyFile: boolean
  isLoading: boolean
  isError: boolean
  previewUrl: string | undefined
  title: string
}

// shared by the inline section and the fullscreen overlay so both render the same states
const PreviewBody: React.FC<PreviewBodyProps> = ({ kind, isEmptyFile, isLoading, isError, previewUrl, title }) => {
  if (isEmptyFile) {
    return <PreviewUnavailable reason="This file is empty and has nothing to preview." />
  }
  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-gray-900">
        <Loader className="text-light-green-500" />
      </div>
    )
  }
  if (isError || !previewUrl) {
    return <PreviewUnavailable reason="This file couldn't be previewed. Download it to view the contents." />
  }
  // keyed on the url so a re-minted sas remounts the preview and its loader
  if (kind === 'image') {
    return <PreviewImage key={previewUrl} url={previewUrl} title={title} />
  }
  if (kind === 'pdf') {
    return <PreviewPdf key={previewUrl} url={previewUrl} />
  }
  return <SandboxedFrame key={previewUrl} src={previewUrl} title={title} />
}

interface DataRoomDocumentDetailProps {
  document: DataRoomDocument
  projectId: string
  projectName: string
  onBack: () => void
}

const DataRoomDocumentDetail: React.FC<DataRoomDocumentDetailProps> = ({
  document,
  projectId,
  projectName,
  onBack,
}) => {
  const { mutate: downloadDocument } = api.project.dataRoom.download.useMutation()
  const { base, ext } = splitFileName(document.name)
  const [isFullscreen, setIsFullscreen] = useState(false)

  // never strand the overlay over a document the user has navigated away from
  useEffect(() => setIsFullscreen(false), [document.id])

  const kind = previewKind(document.name)
  // a cross-origin frame can't be inspected, so an empty file is the only broken case we can detect there
  const isEmptyFile = document.fileSize === 0
  const {
    data: previewUrl,
    isLoading: isPreviewLoading,
    isError: isPreviewError,
  } = api.project.dataRoom.preview.useQuery(projectId, kind !== null && !isEmptyFile ? document.id : null)

  return (
    <div className="flex flex-1 flex-col gap-6 overflow-hidden">
      <div className="px-5 pb-4 pt-8">
        <button
          onClick={onBack}
          className="mb-4 flex items-center gap-2 text-xs text-light-green-500 transition-colors hover:text-light-green-600"
        >
          <IconArrowLeft className="h-4 w-4" />
          Back to all documents
        </button>
        <div className="flex min-w-0 flex-1 items-center">
          <Typography variant="base-bold" className="text-gray-50">
            {base}
          </Typography>
          {ext && <ExtBadge ext={ext} className="ml-2" />}
        </div>
        <div className="mt-1 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Typography variant="xs" className="text-gray-500">
              {formatDate(document.createdAt)}
            </Typography>
            <Typography variant="xs" className="text-gray-500">
              {formatFileSize(document.fileSize)}
            </Typography>
          </div>
          <GradientBorderButton
            gradientSize="sm"
            onClick={() => downloadDocument({ projectId, documentId: document.id })}
          >
            <IconDownload className="h-4 w-4" />
            Download
          </GradientBorderButton>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-5 pb-5">
        {document.description && (
          <div className="flex flex-col gap-2">
            <Typography variant="sm-bold" className=" text-gray-50">
              Description
            </Typography>
            <Typography variant="sm" className="text-gray-200">
              {document.description}
            </Typography>
          </div>
        )}

        {kind !== null && (
          <div className="flex min-h-[420px] flex-1 flex-col gap-2">
            <div className="flex items-center justify-between">
              <Typography variant="sm-bold" className="text-gray-50">
                Document Preview
              </Typography>
              {!isEmptyFile && previewUrl && !isPreviewError && (
                <FullScreenButton onClick={() => setIsFullscreen(true)} />
              )}
            </div>
            <div className="flex-1 overflow-hidden rounded-lg border border-gray-500 bg-white">
              <PreviewBody
                kind={kind}
                isEmptyFile={isEmptyFile}
                isLoading={isPreviewLoading}
                isError={isPreviewError}
                previewUrl={previewUrl}
                title={document.name}
              />
            </div>
            {previewUrl && !isPreviewError && (
              <Typography variant="xs" className="text-gray-500">
                Not rendering correctly? Download the file to view it.
              </Typography>
            )}
          </div>
        )}
      </div>

      {isFullscreen && kind !== null && (
        <FullscreenPreviewDialog
          isOpen={isFullscreen}
          onClose={() => setIsFullscreen(false)}
          fileName={document.name}
          details={[projectName, formatDate(document.createdAt), formatFileSize(document.fileSize)]}
          onDownload={() => downloadDocument({ projectId, documentId: document.id })}
        >
          <div className="h-full bg-white">
            <PreviewBody
              kind={kind}
              isEmptyFile={isEmptyFile}
              isLoading={isPreviewLoading}
              isError={isPreviewError}
              previewUrl={previewUrl}
              title={document.name}
            />
          </div>
        </FullscreenPreviewDialog>
      )}
    </div>
  )
}

export default DataRoomDocumentDetail
